import { z } from "zod";
import { zChaveAlternativa, zDiaCivil, zId, zInstanteUtc, zTipoItem } from "./dominio.js";
import { zAuditoria } from "./srs.js";
/**
 * Sessão de estudo — o motor da F1.
 *
 * **Colisão de nome, resolvida aqui:** `/api/sessao` já é a sessão de
 * autenticação. A sessão de estudo vive em `/api/estudo/*`. Duas coisas
 * diferentes com o mesmo nome numa URL é a origem de um bug que ninguém acha.
 *
 * **O que não desce para o navegador.** Para `multipla`, `certo_errado` e
 * `cloze`, o gabarito e a explicação **não** viajam antes da resposta. Não é
 * zelo excessivo: `acertou` alimenta a fila de erradas, a ofensiva e o SRS, e
 * um gabarito presente no DOM corrompe o registro de quem se distrai — inclusive
 * sem má-fé, porque o navegador guarda e a aba fica aberta.
 *
 * O `cartao` é a exceção, e é uma exceção com razão: o gabarito dele é `{}` e
 * quem julga é o próprio usuário, na escala do SM-2. Não há veredito objetivo a
 * proteger, e o verso é justamente o que ele está tentando lembrar. Segurar o
 * verso obrigaria a uma ida ao servidor no meio da execução — no ônibus, com
 * sinal fraco, exatamente o momento que a direção Polegar existe para atender.
 */
export const CONTEXTOS = ["estudo", "revisao", "erradas", "simulado"];
export const zContexto = z.enum(CONTEXTOS);
// ---------------------------------------------------------------- opções
/**
 * O que existe para filtrar nesta trilha, com a contagem de cada coisa.
 *
 * A tela não inventa as opções nem o que elas alcançam: um filtro que promete
 * "Difícil" e devolve zero itens parece defeito, e um que esconde a contagem
 * faz o usuário montar sessão vazia para descobrir.
 */
export const zOpcoesEstudo = z.object({
    categorias: z.array(z.object({
        id: zId,
        nome: z.string(),
        /** Quantos níveis acima da raiz — a tela indenta com isso. */
        profundidade: z.number().int().nonnegative(),
        /** Itens da categoria e de todos os descendentes. */
        itens: z.number().int(),
    })),
    dificuldades: z.array(z.object({ valor: z.string(), itens: z.number().int() })),
    tipos: z.array(z.object({ valor: zTipoItem, itens: z.number().int() })),
    total: z.number().int(),
    naoRespondidos: z.number().int(),
    naFilaDeErradas: z.number().int(),
});
// --------------------------------------------------------------- montagem
/**
 * Filtros e modificadores da RN-09. Todos opcionais: a sessão padrão é sem
 * filtro nenhum, e o tamanho vem da configuração do servidor, não daqui.
 */
export const zMontarEntrada = z.object({
    categoriaIds: z.array(zId).default([]),
    dificuldades: z.array(z.string().trim().min(1)).default([]),
    tipos: z.array(zTipoItem).default([]),
    /** Itens que nunca foram respondidos. */
    soNaoRespondidos: z.boolean().default(false),
    /** Itens que estão na fila de erradas (RN-11). */
    soErradosAntes: z.boolean().default(false),
    /** Ordena por reincidência, mais errados primeiro (RN-12). */
    priorizarMaisErrados: z.boolean().default(false),
    /**
     * Só o que o SM-2 marcou para hoje ou antes (RN-13).
     *
     * Item sem agendamento **não** entra: ele nunca foi revisado, então não está
     * vencido — está por começar, que é outra lista.
     */
    soDevidosHoje: z.boolean().default(false),
    /**
     * Em que contexto a sessão nasce. Muda o rótulo e o histórico, não a conta:
     * o SM-2 opera sobre o item, não sobre o modo (RN-14).
     */
    contexto: zContexto.default("estudo"),
    /** Ausente = o tamanho padrão da configuração. */
    quantidade: z.number().int().positive().optional(),
});
/** A resposta de "quantos itens este filtro alcança". */
export const zDisponiveis = z.object({ disponiveis: z.number().int().nonnegative() });
// ------------------------------------------------------ o item na execução
const zLacunaVisivel = z.object({ id: z.string().min(1) });
/**
 * O conteúdo como o navegador o recebe **antes** da resposta. Compare com
 * `zCorpoItem`: o cloze perde `resposta` e `aceitaveis`, que são o gabarito
 * disfarçado de conteúdo.
 */
export const zConteudoVisivel = z.discriminatedUnion("tipo", [
    z.object({ tipo: z.literal("multipla"), alternativas: z.partialRecord(zChaveAlternativa, z.string()) }),
    z.object({ tipo: z.literal("certo_errado") }),
    z.object({ tipo: z.literal("cartao"), verso: z.string() }),
    z.object({ tipo: z.literal("cloze"), lacunas: z.array(zLacunaVisivel) }),
]);
export const zItemEmExecucao = z.object({
    id: zId,
    tipo: zTipoItem,
    enunciado: z.string(),
    conteudo: zConteudoVisivel,
    dificuldade: z.string().nullable(),
    /** Caminho da categoria, do mais raso ao mais fundo. */
    categoria: z.array(z.string()),
    /** Texto de apoio compartilhado por vários itens do lote. */
    textoBase: z.string().nullable(),
    /** Eixo independente do acerto que viaja com o item (RN-07). */
    marcado: z.boolean(),
});
// --------------------------------------------------------------- resposta
/** O que o usuário marcou. Vira `resposta.marcado`, discriminado pelo tipo. */
export const zMarcado = z.discriminatedUnion("tipo", [
    z.object({ tipo: z.literal("multipla"), alternativa: zChaveAlternativa }),
    z.object({ tipo: z.literal("certo_errado"), valor: z.boolean() }),
    /** Escala 0–5 do SM-2: não há certo objetivo num cartão. */
    z.object({ tipo: z.literal("cartao"), autoavaliacao: z.number().int().min(0).max(5) }),
    z.object({ tipo: z.literal("cloze"), lacunas: z.record(z.string(), z.string()) }),
]);
/**
 * Se o tipo de item exige **confirmação** antes de consolidar a resposta
 * (RN-05, ADR 021).
 *
 * A distinção mora aqui, e não num `if` dentro da tela, porque ela é regra e
 * não apresentação: o servidor, o script de capturas e qualquer tela futura
 * precisam da mesma resposta, e uma regra copiada em dois lugares diverge no
 * primeiro tipo de item novo.
 *
 * **O critério é haver alternativa a marcar**, e não "ser objetivo":
 *
 * - `multipla` e `certo_errado` têm alternativas na tela. Tocar uma é
 *   *escolher*; consolidar é outro gesto. Enquanto não confirmada, a escolha é
 *   trocável.
 * - `cartao` não tem: a autoavaliação de 0 a 5 **é** a resposta. Não existe
 *   "escolher 4 e confirmar 4" — o 4 já é o juízo que se pediu, e um segundo
 *   toque só repetiria o primeiro.
 * - `cloze` não tem: preencher a lacuna e enviar já são dois gestos, e o envio
 *   já é explícito. Uma confirmação aqui seria o terceiro.
 */
export function exigeConfirmacao(tipo) {
    return tipo === "multipla" || tipo === "certo_errado";
}
export const zResponderEntrada = z.object({
    /**
     * Nasce no navegador e é único (invariante 12). É ele que torna o reenvio
     * inofensivo: a mesma resposta mandada duas vezes grava uma linha só.
     */
    idCliente: z.string().trim().min(8).max(64),
    itemId: zId,
    marcado: zMarcado,
    tempoMs: z.number().int().nonnegative(),
    /**
     * Quando a resposta foi dada de verdade. A tela omite — ali o instante do
     * pedido é o da resposta. Quem manda é a **fila offline**, com o `criadaEm`
     * que já guarda: é o que permite ao servidor aceitar numa sessão expirada a
     * resposta que aconteceu enquanto ela vivia, e o que põe a resposta feita às
     * 23h50 e sincronizada às 00h10 no dia certo (invariante 16).
     */
    respondidoEm: zInstanteUtc.optional(),
});
/**
 * O reagendamento como a tela o recebe.
 *
 * Vai junto a **auditoria inteira**, e não só o número de dias. A exigência da
 * fase é que o intervalo seja conferível: com `intervaloAntes`,
 * `multiplicadorAplicado` e `intervaloDepois`, a tela imprime "6 × 1,2 = 7
 * dias" — a conta, não o resultado. É o que separa SRS auditável de caixa-preta.
 */
export const zAgendamentoVisivel = z.object({
    proximaRevisao: zDiaCivil,
    intervaloDias: z.number().int(),
    fatorFacilidade: z.number(),
    repeticoes: z.number().int(),
    lapsos: z.number().int(),
    /** A frase pronta: "volta em 6 dias". */
    frase: z.string(),
    auditoria: zAuditoria,
    /** Se o usuário já ajustou a dificuldade deste item nesta resposta. */
    ajuste: z.enum(["dificil", "facil"]).nullable(),
});
/** O veredito, que só existe depois da resposta. */
export const zVeredito = z.object({
    itemId: zId,
    acertou: z.boolean(),
    /** O gabarito, revelado agora. `null` para cartão, que não tem certo objetivo. */
    gabarito: z
        .union([
        z.object({ tipo: z.literal("multipla"), correta: zChaveAlternativa }),
        z.object({ tipo: z.literal("certo_errado"), correta: z.boolean() }),
        z.object({ tipo: z.literal("cloze"), lacunas: z.array(z.object({ id: z.string(), resposta: z.string() })) }),
    ])
        .nullable(),
    /** Sempre presente quando o item tem, acertando ou errando (RN-06). */
    explicacao: z.string().nullable(),
    /** `true` quando a resposta já tinha sido gravada — reenvio, não duplicata. */
    repetida: z.boolean(),
    /**
     * O reagendamento pelo SM-2 (RN-13). `null` só quando não houve — o que hoje
     * não acontece, mas o tipo não deve mentir sobre isso.
     */
    agendamento: zAgendamentoVisivel.nullable(),
});
// ------------------------------------------------------------ estado atual
export const zProgresso = z.object({
    total: z.number().int(),
    cursor: z.number().int(),
    respondidos: z.number().int(),
    acertos: z.number().int(),
});
export const zSessaoEmCurso = z.object({
    id: zId,
    contexto: zContexto,
    /**
     * Espelha o enum de `sessao.estado` no schema Drizzle, que é a fonte
     * (invariante 1). Um teste em `apps/api` compara os dois conjuntos, porque
     * daqui não se importa o schema — e enum copiado sem guarda diverge.
     */
    estado: z.enum(["aberta", "concluida", "abandonada", "expirada"]),
    iniciadaEm: zInstanteUtc,
    progresso: zProgresso,
});
export const zEstudoAtual = z.object({
    /** `null` quando não há sessão aberta — estado guiado, não erro. */
    sessao: zSessaoEmCurso.nullable(),
    /** O item na posição do cursor. `null` quando a sessão acabou. */
    item: zItemEmExecucao.nullable(),
    /** Meta do dia, para a barra de progresso saber contra o quê medir. */
    metaDiaria: z.number().int().positive(),
    respondidosHoje: z.number().int(),
    /**
     * `true` só na leitura que acabou de expirar a sessão (RN-64). Sem isto a
     * tela voltaria à montagem sem dizer por que a sessão sumiu, que é
     * indistinguível de nunca ter havido sessão.
     */
    sessaoExpirada: z.boolean(),
});
export const zRespostaDada = z.object({
    veredito: zVeredito,
    /** O estado seguinte, já pronto: uma viagem só por resposta. */
    atual: zEstudoAtual,
});
export const zMontagem = z.object({
    atual: zEstudoAtual,
    /** Quantos itens o filtro alcançou antes do corte pela quantidade. */
    disponiveis: z.number().int(),
    /**
     * Se uma sessão nova foi criada.
     *
     * Existe porque `atual.sessao` não responde a pergunta: quando o filtro não
     * alcança item nenhum, a montagem falha e a **sessão anterior continua
     * aberta** — de propósito, para um filtro mal escolhido não destruir uma
     * sessão em andamento. A tela precisa distinguir "montei" de "achei a que já
     * estava lá", e deduzir isso de `sessao !== null` daria a resposta errada
     * exatamente nesse caso.
     */
    montada: z.boolean(),
});
/**
 * Ajuste opcional da dificuldade, depois do veredito de um item objetivo.
 *
 * O acerto vale q = 4 por padrão, que é o grau neutro — o palpite honesto, já
 * que a máquina não sabe se custou. Estes dois toques corrigem para 3 ou 5 sem
 * obrigar ninguém a nada.
 *
 * **A resposta não é reescrita** (invariante 12): o que muda é o agendamento,
 * recalculado a partir do estado anterior guardado em `ultimaAuditoria`. A
 * `resposta` continua registrando o que se sabia no momento em que foi dada.
 */
export const zAjustarEntrada = z.object({
    itemId: zId,
    ajuste: z.enum(["dificil", "facil"]).nullable(),
});
/**
 * O pacote do modo ônibus: os itens da sessão **com gabarito e explicação**.
 *
 * Esta rota é a única que manda o gabarito antes da resposta, e existe porque
 * responder sem sinal **com feedback** não é possível de outro jeito. Ela só é
 * chamada quando o usuário liga o modo ônibus — desligado, nada muda em relação
 * à F1, onde o gabarito nunca desce.
 */
export const zPacoteOffline = z.object({
    itens: z.array(z.object({
        itemId: zId,
        tipo: zTipoItem,
        /** O conteúdo completo, incluindo as respostas das lacunas do cloze. */
        conteudo: z.unknown(),
        gabarito: z.unknown(),
        explicacao: z.string().nullable(),
    })),
    /** Ecoado para a tela poder dizer o que baixou. */
    baixadoEm: zInstanteUtc,
});
export const zEncerrarEntrada = z.object({
    motivo: z.enum(["concluida", "abandonada"]).default("abandonada"),
});
// ------------------------------------------------------ núcleo determinístico
/**
 * Normaliza texto para comparar resposta de cloze.
 *
 * Tira acento, caixa, pontuação de borda e espaço repetido. Quem digita "2ª
 * forma normal" no celular, com o teclado corrigindo sozinho, não deve errar
 * por causa do acento — o que está sendo medido é a lembrança, não a digitação.
 */
export function normalizarResposta(texto) {
    return texto
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[.,;:!?"'()[\]]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}
/** Uma lacuna acerta quando bate com a resposta ou com qualquer sinônimo aceito. */
export function acertouLacuna(dada, lacuna) {
    if (dada === undefined)
        return false;
    const alvo = normalizarResposta(dada);
    if (alvo === "")
        return false;
    return [lacuna.resposta, ...lacuna.aceitaveis].some((v) => normalizarResposta(v) === alvo);
}
/**
 * Decide se a resposta acertou. Pura: sem banco, sem relógio, sem rede.
 *
 * `limiarAutoavaliacao` vem da configuração, nunca do código (invariante 4). É
 * a nota mínima da escala 0–5 que conta como acerto num cartão.
 */
export function avaliarResposta(gabarito, marcado, limiarAutoavaliacao) {
    switch (marcado.tipo) {
        case "multipla":
            return marcado.alternativa === gabarito.correta;
        case "certo_errado":
            return marcado.valor === gabarito.correta;
        case "cartao":
            return marcado.autoavaliacao >= limiarAutoavaliacao;
        case "cloze": {
            const lacunas = gabarito.lacunas ?? [];
            if (lacunas.length === 0)
                return false;
            // Todas ou nenhuma: meia lembrança de um cloze é lembrança falha, e
            // pontuação parcial tiraria o sentido da fila de erradas.
            return lacunas.every((l) => acertouLacuna(marcado.lacunas[l.id], l));
        }
    }
}
/**
 * Os graus que a tela oferece num cartão, na escala 0–5 do SM-2.
 *
 * **Três, não seis.** 0, 3 e 5 correspondem a coisas que se sentem sem pensar:
 * não veio, veio com esforço, veio na hora. O grau que sai é o 4 — "acertei
 * depois de hesitar" — que é exatamente o que ninguém distingue do 5 no momento
 * da revisão.
 *
 * O custo está escrito em `docs/srs.md` §6: sem o grau neutro, o fator de
 * facilidade se move em toda revisão em vez de às vezes ficar parado. Voltar
 * para quatro é acrescentar uma entrada aqui — `resposta.autoavaliacao` guarda
 * a qualidade bruta, então nada de histórico se perde.
 *
 * Fica no pacote compartilhado para não virar número literal no componente
 * (invariante 10). O limiar que separa acerto de erro vem da configuração.
 */
export const GRAUS_CARTAO = [
    { grau: 0, rotulo: "Não lembrei", ajuda: "Não veio" },
    { grau: 3, rotulo: "Com esforço", ajuda: "Veio, mas custou" },
    { grau: 5, rotulo: "Tranquilo", ajuda: "Veio na hora" },
];
/**
 * Parte o enunciado nos marcadores `{{id}}`.
 *
 * Fica aqui, e não no componente, por dois motivos: é lógica de texto e merece
 * teste sem DOM, e o importador da etapa 4 vai precisar da mesma leitura para
 * avisar sobre lacuna declarada que não aparece no enunciado.
 */
export function partirCloze(enunciado) {
    const pedacos = [];
    const padrao = /\{\{\s*([^}\s]+)\s*\}\}/g;
    let ultimo = 0;
    for (const m of enunciado.matchAll(padrao)) {
        const inicio = m.index;
        if (inicio > ultimo)
            pedacos.push({ tipo: "texto", valor: enunciado.slice(ultimo, inicio) });
        pedacos.push({ tipo: "lacuna", id: m[1] });
        ultimo = inicio + m[0].length;
    }
    if (ultimo < enunciado.length)
        pedacos.push({ tipo: "texto", valor: enunciado.slice(ultimo) });
    return pedacos;
}
//# sourceMappingURL=estudo.js.map